import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@panel/commons';
import { useOnScrollEvent, useIsOnScreen, useQuery, useModal } from '@panel/commons/hooks';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { currencyOptionsBySite, site } from '@panel/commons/helpers';
import styled from 'styled-components';
import { useLocation, useHistory } from 'react-router-dom';
import { getColor } from '@panel/commons/core/themev3';
import {
  DetailsContainer,
  Separator,
  Title,
  DetailItemsContainer,
  DetailItem,
  ToggleButton,
  ContainerToggle,
  DetailsContainerToObserve,
  StyledLink
} from '../../styles';
import {
  getOrderData,
  getProductTaxes,
  selectCurrentProduct,
  selectProductTaxes,
  selectProductTaxesParams
} from '../../../../../ducks/step4Module2';
import { Step4Module1Selectors } from '../../../../../ducks/step4Module1';
import CouponField from './coupon';
import { useStepperCtx } from '../../../../stepper';
import { Modal } from '../../../../Modals/ModalWrapper';
import { LeavePaymentForm } from '../../../../Modals/leavePaymentForm';
import { getWithUpgrade } from '../../../../../utils/misc';

const currentCurrency = currencyOptionsBySite(site.id)[0];

function calculateSubtotal(taxes, subTotal, taxName) {
  let currentSubTotal = `${currentCurrency.name} ${subTotal}`;
  if (taxes || site.isBrasil) {
    return <span>{currentSubTotal}</span>;
  }
  currentSubTotal = `${currentSubTotal} ${taxName}`;
  return (
    <span>
      {currentSubTotal} <sup>*</sup>
    </span>
  );
}

const DeleteBtn = styled.button`
  :hover {
    cursor: pointer;
  }
`;

function PurchaseDetails() {
  const [toggleDetail, setToggleDetail] = useState(false);
  const [withUpgrade, setWithUpgrade] = useState(false);
  const {
    labelTotal,
    labelTaxes,
    labelPercent,
    discount,
    labelDiscount,
    labelDiscountPercent,
    labelUpgradeDiscount
  } = useSelector(selectProductTaxes);
  const { currentPlan, eligibleForUpgrade, products } = useSelector(Step4Module1Selectors.getStep4Module1State);
  const currentProduct = useSelector(selectCurrentProduct);
  const { userId, productId } = useSelector(selectProductTaxesParams);
  const order = useSelector(getOrderData);
  const { trigger, getValues } = useFormContext();
  const { preventTransition, setPreventTransition } = useStepperCtx();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { isShowing, toggleValue } = useModal();
  const location = useLocation();
  const { push } = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ref = useRef();
  const onScreen = useIsOnScreen(ref);
  const displaySticky =
    !site.isPanama && !onScreen && !(window.innerHeight + window.scrollY >= document.body.offsetHeight);
  const displayDetail = !/pago\/error|exito/.test(location.pathname);
  const currentPlanId = eligibleForUpgrade && currentPlan && withUpgrade ? currentPlan.publication_plan_id : '';
  const currentPlanName = currentPlan ? currentPlan.publication_areas[0].name : '';
  const totalAmount = labelTotal || currentProduct.formatUnitPrice;
  const isTotal = labelPercent || site.isBrasil;
  const currentCurrencySymbol = site.isArgentina
    ? `${currentCurrency.value} ${currentCurrency.name}`
    : currentCurrency.name;

  useEffect(() => {
    if (eligibleForUpgrade && products.length > 0 && !withUpgrade) {
      setWithUpgrade(getWithUpgrade(products));
    }
  }, [products, eligibleForUpgrade, withUpgrade]);

  const collapseDetail = () => {
    if (toggleDetail) {
      setToggleDetail(false);
    }
  };

  const refetchTaxes = (query = {}) => {
    const userType = currentProduct.priceList;
    if (userId && productId && userType) {
      const queryParams = { products: productId, userType, currentPlanId, postingId, ...query };
      dispatch(getProductTaxes(userId, queryParams));
    }
  };

  const onValidateCoupon = () => {
    const result = trigger('coupon', { shouldFocus: true });
    if (result) {
      const couponVal = getValues('coupon');
      if (couponVal) {
        refetchTaxes({ coupon: couponVal });
      } else {
        refetchTaxes();
      }
    }
  };

  const handleRedirect = () => {
    push(`/publicador/planes/seleccion${postingId ? `?postingId=${postingId}` : ''}`);
  };
  const handleModalStatus = status => {
    toggleValue(status);
  };

  const handleModalRedirect = () => {
    handleModalStatus(false);
    setPreventTransition(null);
    handleRedirect();
  };

  useOnScrollEvent(!onScreen, collapseDetail);

  return (
    <>
      <DetailsContainerToObserve show={displayDetail} ref={ref}>
        <DetailsContainer isSticky={displaySticky}>
          <ContainerToggle show={toggleDetail || onScreen}>
            <DetailItemsContainer>
              <Title icon='cart' main>
                {t('publisher.step4.module2.details.title')}
              </Title>
              {toggleDetail && !onScreen && (
                // eslint-disable-next-line react/jsx-boolean-value
                <ToggleButton icon='chevronUp' onClick={() => setToggleDetail(false)} expanded={!toggleDetail}>
                  {t('publisher.step4.module2.details.hide')}
                  <Icon
                    data-repa='step4-images-collapse-icon'
                    name={!toggleDetail ? `chevronDown` : `chevronUp`}
                    width={20}
                    height={20}
                    colorFill={getColor('primary')}
                    viewBox='0 0 16 16'
                  />
                </ToggleButton>
              )}
            </DetailItemsContainer>
            <Separator />
            {/* PLAN */}
            <DetailItemsContainer>
              <DetailItem>{currentProduct.name}</DetailItem>
              <DetailItem>
                {calculateSubtotal(
                  labelTaxes,
                  currentProduct.formatUnitPrice,
                  t('publisher.step4.module2.details.plusTaxes')
                )}
              </DetailItem>
              <DetailItem color='darkGray' wrapped>
                {currentProduct.title}
              </DetailItem>
              {site.isArgentina && (
                <DetailItem color='darkGray' wrapped>
                  {t('publisher.step4.module2.details.NoTax')}
                </DetailItem>
              )}
            </DetailItemsContainer>
            {/* FIN PLAN */}
            {/* UPGRADE */}
            {eligibleForUpgrade && labelUpgradeDiscount && withUpgrade && (
              <DetailItemsContainer>
                <DetailItem color='positive'>Plan {currentPlanName}</DetailItem>
                {labelUpgradeDiscount && (
                  <DetailItem color='positive'>
                    -{currentCurrency.name}
                    {labelUpgradeDiscount}
                  </DetailItem>
                )}

                <DetailItem color='darkGray' wrapped>
                  {t('publisher.step4.module1.currentPlan.title')}
                </DetailItem>
              </DetailItemsContainer>
            )}
            {/* FIN UPGRADE */}
            {/* DESCUENTO */}
            {discount ? (
              <DetailItemsContainer>
                <DetailItem color='positive'>{`${t(
                  'publisher.step4.module2.coupon.box.label'
                )} (-${labelDiscountPercent}%)`}</DetailItem>
                <DetailItem color='positive'>{`-${currentCurrency.name}${labelDiscount}`}</DetailItem>
                <DetailItem color='blue' wrapped>
                  <DeleteBtn type='button' onClick={() => refetchTaxes()}>
                    {t('publisher.step4.module2.field.coupon.delete')}
                  </DeleteBtn>
                </DetailItem>
              </DetailItemsContainer>
            ) : null}
            {/* FIN DESCUENTO */}
            {!onScreen && (
              <DetailItemsContainer>
                <StyledLink onClick={() => (preventTransition ? handleModalStatus(true) : handleRedirect())}>
                  {t('publisher.step4.module2.details.editPlan')}
                </StyledLink>
              </DetailItemsContainer>
            )}
            {site.isPanama && labelTaxes && (
              <>
                <DetailItemsContainer className='capa'>
                  <DetailItem>{t('publisher.step4.module2.details.itbmsTaxes')}</DetailItem>
                  <DetailItem>{`${currentCurrency.name} ${labelTaxes}`}</DetailItem>
                </DetailItemsContainer>
                {order && (
                  <DetailItemsContainer className='capa'>
                    <DetailItem capa>{t('publisher.step4.module2.details.orderCode')}</DetailItem>
                    <DetailItem capa>{order.orderId}</DetailItem>
                  </DetailItemsContainer>
                )}
                <DetailItemsContainer className='capa'>
                  <DetailItem capa>{t('publisher.step4.module2.details.publicationCode')}</DetailItem>
                  <DetailItem capa>{postingId}</DetailItem>
                </DetailItemsContainer>
              </>
            )}
            {/* IMPUESTOS */}
            {!site.isPanama && labelPercent ? (
              <DetailItemsContainer className='taxes'>
                <DetailItem>{`${t('publisher.step4.module2.details.taxes')} (${labelPercent}%)`}</DetailItem>
                <DetailItem>{`${currentCurrency.name} ${labelTaxes}`}</DetailItem>
              </DetailItemsContainer>
            ) : null}
            {/* FIN IMPUESTOS */}
          </ContainerToggle>
          {(onScreen || toggleDetail) && <Separator className={site.isPanama ? `capa` : ` `} />}
          {/* TOTAL */}
          {!toggleDetail && !onScreen && (
            <>
              <DetailItemsContainer isSticky={!onScreen}>
                <ToggleButton icon='chevronDown' onClick={() => setToggleDetail(true)} expanded={!toggleDetail}>
                  {t('publisher.step4.module2.details.show')}
                  <Icon
                    data-repa='step4-images-collapse-icon'
                    name={!toggleDetail ? `chevronDown` : `chevronUp`}
                    width={20}
                    height={20}
                    colorFill={getColor('primary')}
                    viewBox='0 0 16 16'
                  />
                </ToggleButton>
              </DetailItemsContainer>
            </>
          )}
          <DetailItemsContainer className={site.isPanama ? `totalWithoutCoupon` : `total`}>
            <DetailItem price>
              {isTotal ? t('publisher.step4.module2.details.total') : t('publisher.step4.module2.details.subtotal')}
            </DetailItem>
            <DetailItem price>
              {`${currentCurrencySymbol} ${totalAmount}`}
              {!isTotal || (eligibleForUpgrade && withUpgrade && '*')}
            </DetailItem>
            {eligibleForUpgrade &&
              withUpgrade &&
              (!currentProduct.name.toLowerCase().includes('trimestral') || !site.isPeru) && (
                <DetailItem color='darkGray' upgrade>
                  {t('publisher.step4.module2.details.upgrade.clarificationOfTotal')}
                  <span>
                    {currentCurrency.name}
                    {currentProduct.formatUnitPrice}
                  </span>
                </DetailItem>
              )}
          </DetailItemsContainer>
          {/* FIN TOTAL */}
          {/* INPUT DESCUENTO */}
          {onScreen && !site.isPanama && <CouponField onValidateCoupon={onValidateCoupon} />}
          {/* FIN INPUT DESCUENTO */}
        </DetailsContainer>
      </DetailsContainerToObserve>
      {isShowing && (
        <Modal>
          <LeavePaymentForm
            openModal={isShowing}
            onClose={() => handleModalStatus(false)}
            handleRedirect={handleModalRedirect}
          />
        </Modal>
      )}
    </>
  );
}

export default PurchaseDetails;
